import {Component, DestroyRef, EventEmitter, inject, Input, NgZone, OnInit, Output} from '@angular/core';
import {Config} from "../../../config/config";
import {WorkspaceService} from "../../../workspace/workspace.service";
import {DbResponse} from "../../interceptors/db-response";
import {filter, first, switchMap} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {DbStoreState} from "../../../store";
import * as fromStore from "../../../store";
import {PaymentMethod} from "../../../user/payment-method";
import {NotifierComponent} from "../notifier/notifier.component";
import {Notification} from "../notifier/notification";
import {NotificationType} from "../notifier/notification-type";
import {TypeUtils} from "../../utils/type-utils";
import {AuthService} from "../../../auth/auth.service";
import {RequesterService} from "../../services/requester/requester.service";
import {DbServerErrors} from "../../interceptors/db-server-errors";
import {GoogleAnalyticsService} from "../../services/google-analytics/google-analytics.service";
import {UserSubscription, UserSubscriptionType} from "../../../user/user-subscription";
import {ChosenPlan} from "./models/plan.model";
import {CouponCode} from "../../../workspace/billing/coupon";
import {User} from "../../../user/user";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {CheckoutService} from "../../../auth/checkout/checkout.service";
import {LanguageService} from "../../services/language/language.service";

export type PaidFeatureUpgradeMode = 'ssh' | 'cloudflare' | 'regenerate' | 'pro-level' | 'passwordProtect'  | 'professional';

@Component({
    selector: 'db-payment-info',
    templateUrl: './payment-info.component.html',
    styleUrls: ['./payment-info.component.scss'],
    standalone: false
})
export class PaymentInfoComponent implements OnInit {

  @Input() subscription: ChosenPlan;
  @Input() user?: User;
  @Input() demoUser?: boolean;
  @Input() currentSubscription?: UserSubscription;
  @Input() feature?: string;
  @Input() hidePaymentOptions?: boolean = false;
  @Input() hidePaypal: boolean = true;
  @Input() invited?: boolean;
  @Input() paypalAsDefault?: boolean;
  @Input() showOnlyDefaultPayment?: boolean;
  @Input() mode: PaidFeatureUpgradeMode;
  @Input() isPaypalInvalidCode: boolean;
  @Input() couponCode?: string;
  @Input() totalPayNow?: any;
  @Input() chosenPlan?: any;
  @Input() newPrice?: any;
  @Input() coupon: CouponCode;
  @Input() forDemo: boolean = false;
  @Output() onClose: EventEmitter<void | 'upgraded'> = new EventEmitter<void | 'upgraded'>();
  @Output() onLoading: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() paymentIsPaypal: EventEmitter<boolean> = new EventEmitter<boolean>();
  public cardType: string = '';
  public paymentMethod: string = 'card';
  public stripeErrors: string | null = null;
  public serverErrors: DbServerErrors;
  public loading: boolean;
  public useNewPaymentMethod: boolean;
  public activeTab: string;
  private _destroyRef: DestroyRef = inject(DestroyRef);

  private _stripe;
  private _cardNumber;
  public paymentMethods: PaymentMethod[];
  public selectedPaymentMethod: PaymentMethod;
  public hidePaypalAB: boolean = true;
  public fromPricingCheckout: boolean = false;
  public _confirmationToken: string;
  public stripeError: boolean = false;
  public isMobile = window.innerWidth <= 991;

  constructor(
    private _zone: NgZone,
    private _store: Store<DbStoreState>,
    private _authService: AuthService,
    private _workspaceService: WorkspaceService,
    private _requester: RequesterService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _checkoutService: CheckoutService,
    private _languageService: LanguageService
  ) {
  }

  ngOnInit() {
    /*const getExperimentalCookie = this._cookieService.get('db-experiment');
    this.hidePaypalAB = getExperimentalCookie ? JSON.parse(getExperimentalCookie) && JSON.parse(getExperimentalCookie).hidePaypal &&  JSON.parse(getExperimentalCookie).hidePaypal === 'b' : false;*/
    this.hidePaypalAB = !(this.currentSubscription && this.currentSubscription.paymentType === 'paypal');
    this.activeTab = this.feature === 'ssh' && this.currentSubscription && this.currentSubscription.type !== UserSubscriptionType.FREE ? 'existingCards' : 'addCard';
    if (!this.invited) {
      this._store.dispatch(new fromStore.LoadUserPaymentMethods());
      this.getPaymentMethods();
    }
    this.bindStripe();
    if (this.paypalAsDefault) {
      this.paymentMethod = 'paypal';
    }
    this._handleGoogleApple();
  }

  public bindStripe(): void {
    this._zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.initStripe();
      }, 250);
    });
  }

  public selectPaymentMethod($event: any): void {
    this.selectedPaymentMethod = $event.paymentMethod;
  }

  public onPaymentMethodChange(method: string): void {
    this.paymentMethod = method;
    if (method === 'card') {
      this.bindStripe();
      this.paymentIsPaypal.emit(false);
    } else if (method === 'paypal') {
      this.paymentIsPaypal.emit(true);
    }
  }

  public changeTab(tabId) {
    if (this.activeTab !== tabId) {
      this.useNewPaymentMethod = !this.useNewPaymentMethod;
    }
    this.activeTab = tabId;
  }

  private initStripe(): void {
    if (window['Stripe']) {
      const stripeConfig: any = {};

      // Set locale from language service
      const currentLanguage = this._languageService.getCurrentLanguage();
      if (currentLanguage) {
        stripeConfig.locale = currentLanguage;
      }

      this._stripe = window['Stripe'](Config.stripeKey, stripeConfig);
    }
    const elements: any = this._stripe.elements();

    const elementStyles = {
      base: {
        iconColor: '#666EE8',
        color: '#555555',
        lineHeight: '40px',
        fontWeight: 400,
        fontFamily: '"Open Sans", sans-serif',
        fontSize: '14px',

        '::placeholder': {
          color: '#555555',
        },
      },
    };
    this._cardNumber = elements.create('card', {
      hideIcon: true,
      style: elementStyles
    });
    this._cardNumber.mount('#stripe');
    this.cardType = "";

    this._cardNumber.on('change', (event) => {
      this.cardType = event.brand;
    });
    (<HTMLInputElement>document.getElementsByClassName('__PrivateStripeElement-input')[0]).style.minHeight = '36px';
    (<HTMLInputElement>document.getElementsByClassName('__PrivateStripeElement')[0]).style.outline = 'none';
  }

  private getPaymentMethods(): Promise<null> {
    this.useNewPaymentMethod = true;

    return new Promise<null>((resolve: any, reject: any): void => {

      this._store.select(fromStore.getUserPaymentMethodsLoaded)
        .pipe(
          switchMap(() => this._store.select(fromStore.getUserPaymentMethods)),
          first(data => data && !TypeUtils.isNull(data))
        )
        .subscribe(data => {
          if (data && !TypeUtils.isNull(data)) {
            if (this.showOnlyDefaultPayment) {
              this.paymentMethods = data.filter((paymentMethod: PaymentMethod): boolean => paymentMethod.active);
            }
            else this.paymentMethods = data;
            if (this.paymentMethods && this.paymentMethods.length) {
              this.useNewPaymentMethod = false;
              this.activeTab = 'existingCards';
            } else {
              this.activeTab = 'addCard';
            }
            if (this.selectedPaymentMethod) {
              this.selectedPaymentMethod = this.paymentMethods.find((paymentMethod: PaymentMethod): boolean => paymentMethod.id === this.selectedPaymentMethod.id);
            }
            if (!this.selectedPaymentMethod) {
              this.selectedPaymentMethod = this.paymentMethods.find((paymentMethod: PaymentMethod): boolean => paymentMethod.active);
            }

            if (this.useNewPaymentMethod && this.paymentMethods.length) {
              this.selectedPaymentMethod = this.paymentMethods[this.paymentMethods.length - 1];
            }

            resolve();
          }
          else {
            NotifierComponent.notify(new Notification("Something went wrong...", NotificationType.DANGER));
            reject();
          }
        });
    });
  }

  public checkoutHandler(coupon?, fromPricingCheckout?): void {
    this.coupon = coupon;
    this.onLoading.emit(true);
    this.fromPricingCheckout = fromPricingCheckout;
    this.paymentMethod === 'card' ? this.checkoutWithCard() : this.checkoutWithPayPal();
  }

  private checkoutWithPayPal(): void {
    this.doAction('paypal_recurring');
  }

  private _handleGoogleApple(): void {
    this._checkoutService.GoogleApple
      .pipe(
        takeUntilDestroyed(this._destroyRef))
      .subscribe(value => {
        if (value) {
          this._confirmationToken = value;
          this.onLoading.emit(true);
          this.checkoutWithCard('google_pay');
        }
      });
  }

  private checkoutWithCard(mode = 'stripe'): void {
    const needToVerify = this.feature === 'ssh' || (this.coupon && this.coupon.couponCode && !this.coupon.freeDaysBeforePayment) || (this.currentSubscription && this.currentSubscription.trialBoosterClient) ? 0 : 1;
    this.stripeErrors = null;
    const cardId = this.selectedPaymentMethod ? this.selectedPaymentMethod.id : null;
    if (this.activeTab && this.activeTab === "addCard" || mode === 'google_pay') {
      if (mode === 'google_pay') {
        this._addPaymentMethod(this._confirmationToken, needToVerify, 'card', mode);
      }
      else {
        this._stripe.createPaymentMethod('card', this._cardNumber).then((response) => {
          if (response.paymentMethod) {
            this._addPaymentMethod(response.paymentMethod.id, needToVerify, 'card');
          } else {
            this.stripeErrors = response.error.message;
            this.onLoading.emit(false);
          }
        });
      }
    } else {
      this.doAction("card", cardId);
    }
  }

  private _addPaymentMethod(paymentMethodId: string, needToVerify: number, paymentMethod = 'card', mode = 'stripe'): void {
    this._workspaceService.addPaymentMethod()
      .pipe(
        filter(_response => !!_response),
        first()
      )
      .subscribe(_response => {
        if (_response.success) {
          if (_response.data && _response.data.requires_source_action) {
            this.handleAction(_response.data, needToVerify ? 'add_card' : 'force_charge');
          } else {
            this._store.dispatch(new fromStore.LoadUserPaymentMethods());
            this.getPaymentMethods()
              .then((): void => {
                this.doAction("card", _response.data.id);
              })
              .catch((): void => {
                this.onLoading.emit(false);
              });
          }
        } else {
          this.serverErrors = _response.serverErrors;
          this.onLoading.emit(false);
        }
      });
  }

  public doAction(paymentType: string, paymentMethodId?): void {
    for (const key of Config.paypalLocalKeys) {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
      }
    }
    if (this.feature === 'getDiscount' || (this.feature === 'ssh' && this.currentSubscription && this.currentSubscription.type !== UserSubscriptionType.FREE)) {
      this.forceCharge(paymentType, paymentMethodId);
    } else {
      this.upgrade(paymentType, paymentMethodId);
    }
  }

  public forceCharge(paymentType: string, paymentMethodId: any): void {
    this._store.dispatch(new fromStore.LoadUserAvailableSubscriptions());
    this._workspaceService.forceCharge((paymentType === 'card' || paymentType === 'stripe') ? 'card' : 'paypal', paymentMethodId, TypeUtils.isDefined(this.couponCode) ? this.couponCode : null)
        .pipe(
            filter((response: DbResponse): boolean => !!response),
            first())
        .subscribe((response) => {
        if (response.success) {
          if (response.data && response.data.approval_link) {
            window.location.replace(response.data.approval_link);
          } else if (response.data.result['payment_status'] === 'Confirmed') {
            this._store.dispatch(new fromStore.LoadUser());
            this._store.dispatch(new fromStore.UpdateUserSubscription());
            this.onClose.emit();
          } else if (response.data && response.data.result && response.data.result.requires_source_action) {
            this._store.dispatch(new fromStore.LoadUser());
            this._store.dispatch(new fromStore.UpdateUserSubscription());
            this.loading = false;
            this.handleAction(response.data, 'force_charge');
          }
        } else {
          this.serverErrors = response.serverErrors;
          this.loading = false;
        }
      });
  }

  public upgrade(paymentType: string, paymentMethodId = null): void {
    const coupon = this.coupon && this.coupon.couponCode ? this.coupon.couponCode : ((this.currentSubscription && this.currentSubscription.type === UserSubscriptionType.FREE
      && !this.currentSubscription.trialBoosterClient && this.feature !== 'ssh' && !this.fromPricingCheckout) ? '1wf' : null);
    const _cardId = paymentMethodId ? paymentMethodId : (this.selectedPaymentMethod ? this.selectedPaymentMethod.id : null);
    this._workspaceService.upgradeSubscription(this.subscription.id, null, coupon, _cardId, paymentType, this.subscription.title)
      .pipe(
        filter(response => !!response),
        first()
      )
      .subscribe((response): void => {
        if (response.success) {
          if (paymentType === 'paypal_recurring' && response.data && response.data.result.approval_link) {
            if (this.feature === 'booster') {
              localStorage.setItem('dbFromBoosterPopupPaypal', '1');
              window.location.replace(response.data.result.approval_link);
            } else {
              localStorage.setItem('dbFrom1MFPopupPaypal', '1');
              window.location.replace(response.data.result.approval_link);
            }
          }
          else if (response.data && response.data.result && (!TypeUtils.isUndefined(response.data.result.requires_source_action) || !TypeUtils.isUndefined(response.data.result.payment_status))) {
            if (response.data.result['payment_status'] === 'Confirmed') {
              this.onClose.emit('upgraded');
              this._store.dispatch(new fromStore.LoadUser());
              this._store.dispatch(new fromStore.UpdateUserSubscription());
              this._store.dispatch(new fromStore.LoadUserAvailableSubscriptions());
              this.sentSuccessCheckoutEvent();
            } else {
              this.handleAction(response.data, 'upgrade');
            }
          } else {
            this.getClientPayments('upgrade');
          }
        } else {
          if (response.serverErrors && response.serverErrors.message === 'This card is already in use. Please use a different card.') {
            this._store.dispatch(new fromStore.LoadUserPaymentMethods());
            this._cardNumber.clear();
            this.selectedPaymentMethod = null;
            this.stripeError = true;
          }
          this.serverErrors = response.serverErrors;
          this.onLoading.emit(false);
        }
      });
  }

  private handleAction(_data, action) {
    this._stripe.handleCardAction(_data['client_secret'] || _data.result['client_secret'] || _data.result['payment_status']).then((result) => {
      if (result.error && result.error.payment_intent) {
        this.confirmClientStripe(result.error.payment_intent.id, action, _data);
      } else {
        // The card action has been handled
        // The PaymentIntent can be confirmed again on the server
        this.confirmClientStripe(result.paymentIntent.id, action, _data);
      }
    });
  }

  public confirmClientStripe(id, action, _data?): void {
    this._authService.confirmStripe(id, action)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first()
      )
      .subscribe((response) => {
        if (response.success) {
          if (action === 'add_card') {
            this.doAction("card");
          }
          this.getClientPayments('upgrade');
        } else {
          this.serverErrors = response.serverErrors;
        }
        this.onLoading.emit(false);
      });
  }

  public getClientPayments(action?: string): void {
    this._getPayments(action);
    this._requester.clearCache();
    this._store.dispatch(new fromStore.LoadUser());
    this._store.dispatch(new fromStore.UpdateUserSubscription());
  }

  private _getPayments(action?): void {
    if (!this.stripeError) {
      if (action && action === 'upgrade') {
        this.onClose.emit('upgraded');
        this._store.dispatch(new fromStore.LoadUserAvailableSubscriptions());
      } else if (!action || (action && action !== 'force_charge')) {
        this.onClose.emit();
      }
      this.sentSuccessCheckoutEvent(action);
    }
  }

  private sentSuccessCheckoutEvent(action?): void {
    const analyticsData = {
      eventCategory: action === 'force_charge' ? 'Force Charge(ssh/sftp)' : this.feature === 'booster' ? 'Booster' +
        ' Upgrade Offer' : this.mode === 'pro-level' ? 'Dashboard Action' : !this.demoUser? 'Free trial paywalls' : 'Free Upgrade Offer',
      eventAction: 'Checkout Page',
      eventLabel: this.mode === 'pro-level' ? 'Checkout Completed (Pro level pop-up)' : 'Checkout Completed'
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

}
